'use client';

import { type FC, useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { FaSignOutAlt, FaPlus, FaCoins, FaChevronDown } from '../icons';
import { formatCreditsNumber } from '../../utils/formatters';
import { useProfile, useBalance } from '../../queries/useProfileQueries';
import { useActiveBuff, useGlobalBuff } from '../../queries/useHomeQueries';
import { useOnlineCount } from '../../queries/useOnlineCount';
import { useOnlineCountSubscription } from '../../hooks/useOnlineCountSubscription';
import { useLanguage } from '../../hooks/useLanguage';
import { languages, type Language } from '../../stores/languageStore';
import { useCapability } from '@/runtime/useCapability';
import { useLogout } from '../../hooks/useLogout';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { useNavigation } from '../../contexts/NavigationContext';
import { useServerHasAuth } from '../../contexts/ServerAuthContext';
import { useAuthStore } from '../../stores/authStore';
import { useRawInitDataReady } from '../../hooks/useRawInitDataWithDev';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { cn } from '../../utils/cn';
import translations from './DesktopHeader.translations';

const languageFlags: Record<Language, string> = {
  en: '🇬🇧',
  ru: '🇷🇺',
  uz: '🇺🇿',
};

const getTimeLeft = (expiresAt: string) => {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, isExpired: true };
  return {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    isExpired: false,
  };
};

export const DesktopHeader: FC = () => {
  const { data: profile } = useProfile();
  const balance = useBalance();
  const { data: activeBuff } = useActiveBuff();
  const { data: globalBuff } = useGlobalBuff();
  const { data: onlineCountApi } = useOnlineCount();
  const onlineCountWs = useOnlineCountSubscription();
  const onlineCount = onlineCountWs ?? onlineCountApi;
  const { language, changeLanguage } = useLanguage();
  const canLogout = useCapability('logoutAction');
  const logout = useLogout();
  const navigation = useNavigation();
  const { t } = useLocalTranslation(translations);

  // Pick the auth shape from the server-set cookie until React Query
  // + Zustand finish hydrating. Without this the header's first
  // render trusts `!!profile`, which is undefined for the very first
  // client paint (queryClient hasn't applied dehydrated state yet on
  // some browsers / startup sequences) — so the user sees the
  // anonymous "Войти" CTA flash before the authed balance/avatar
  // takes over. After hydration we trust `profile` directly so token
  // swap or logout mid-session still updates the chrome.
  const serverHasAuth = useServerHasAuth();
  const hydrated = useAuthStore((s) => s.hydrated);
  const initDataReady = useRawInitDataReady();
  const isAuthed = (!hydrated || !initDataReady) ? serverHasAuth : !!profile;

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const [buffTime, setBuffTime] = useState({ hours: 0, minutes: 0, seconds: 0, isExpired: true });

  const showPromocodeBuff = (activeBuff && activeBuff.isValid) || (globalBuff && globalBuff.isActive);
  const buffExpiresAt = activeBuff?.expiresAt || globalBuff?.endDate;
  const buffPercentage = activeBuff?.bonusPercentage || globalBuff?.bonusPercentage || 0;
  const hasBuffTimer = showPromocodeBuff && buffExpiresAt && !buffTime.isExpired;
  const buffTimerStr = hasBuffTimer
    ? `${String(buffTime.hours).padStart(2, '0')}:${String(buffTime.minutes).padStart(2, '0')}:${String(buffTime.seconds).padStart(2, '0')}`
    : null;

  useEffect(() => {
    if (!buffExpiresAt) return;
    const update = () => setBuffTime(getTimeLeft(buffExpiresAt));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [buffExpiresAt]);

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  return (
    <header className="border-b border-white/[0.06] h-[72px] flex items-center justify-between shrink-0 pl-[calc(1.5rem+var(--tg-viewport-safe-area-inset-left,0px))] pr-[calc(1.5rem+var(--tg-viewport-safe-area-inset-right,0px))]">
      {/* Left: Logo + Online */}
      <div className="flex items-center gap-4">
        <span
          className="font-sans text-[27px] tracking-widest uppercase select-none"
          style={{
            fontWeight: 800,
            letterSpacing: '0.18em',
            background: 'linear-gradient(to top, #7C3AED 0%, #A78BFA 50%, #00C8FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.5)) drop-shadow(0 0 10px rgba(0,200,255,0.3))',
          }}
        >
          SHOT
        </span>
        {onlineCount != null && onlineCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-sm text-emerald-400/80 font-medium tracking-wide">online</span>
            <span className="text-sm font-bold text-white/50 tabular-nums">{onlineCount}</span>
          </div>
        )}
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Language picker */}
        <div ref={langRef} className="relative">
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-transparent hover:bg-white/[0.05] border-none cursor-pointer transition-colors"
            onClick={() => setLangOpen(!langOpen)}
          >
            <span className="text-lg">{languageFlags[language]}</span>
            <FaChevronDown className="text-[9px] text-white/30" />
          </button>
          {langOpen && (
            <div className="absolute top-full right-0 mt-1 bg-[#0c0f16] border border-white/[0.08] rounded-xl overflow-hidden z-50 min-w-[140px] shadow-[0_12px_40px_rgba(0,0,0,0.7)]">
              {(Object.entries(languages) as [Language, string][]).map(([code, name]) => (
                <button
                  key={code}
                  className={cn(
                    'flex items-center gap-2 w-full px-3 py-2 text-xs border-none cursor-pointer transition-colors text-left',
                    code === language
                      ? 'bg-white/[0.06] text-white'
                      : 'bg-transparent text-white/50 hover:bg-white/[0.03] hover:text-white/80',
                  )}
                  onClick={() => { changeLanguage(code); setLangOpen(false); }}
                >
                  <span>{languageFlags[code]}</span>
                  <span>{name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="w-px h-7 bg-white/[0.06]" />

        {isAuthed ? (
          <>
            {/* Balance — single clickable block with coin + amount + plus icon */}
            <button
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] cursor-pointer transition-colors"
              onClick={() => navigation.navigate('payment')}
            >
              <FaCoins className="text-amber-400 text-sm" />
              <span className="text-white font-bold text-base tabular-nums tracking-tight">
                {formatCreditsNumber(balance)}
              </span>
              <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-emerald-500 ml-1">
                <FaPlus className="text-white text-[10px]" />
              </div>
            </button>

            {/* Buff badge */}
            {showPromocodeBuff && (
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 cursor-pointer transition-colors hover:bg-emerald-500/20"
                onClick={() => navigation.navigate('payment')}
              >
                <span className="text-sm font-bold text-emerald-400">+{buffPercentage}%</span>
                {buffTimerStr && <span className="text-xs text-emerald-400/60 tabular-nums">{buffTimerStr}</span>}
              </button>
            )}

            {/* Separator */}
            <div className="w-px h-7 bg-white/[0.06]" />

            {/* Profile button: avatar only */}
            <button
              className="flex items-center justify-center p-1 rounded-full bg-transparent hover:bg-white/[0.04] border-none cursor-pointer transition-all hover:scale-105"
              onClick={() => { navigation.changeTab('profile'); navigation.resetStack('profile', 'profile'); }}
            >
              <img
                src={profile?.id ? `${getApiBaseUrl()}/api/users/avatar/${profile.id}` : undefined}
                alt=""
                className="w-9 h-9 rounded-full object-cover bg-white/10"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </button>

            {/* Logout */}
            {canLogout && (
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-transparent hover:bg-white/[0.05] border-none cursor-pointer transition-colors text-white/25 hover:text-red-400"
                onClick={logout}
              >
                <FaSignOutAlt size="14" />
              </button>
            )}
          </>
        ) : (
          <Link
            href="/login"
            className="px-4 py-2 rounded-2xl bg-[#22c55e] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
          >
            {t('login')}
          </Link>
        )}
      </div>
    </header>
  );
};
